import { useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface UseWebSocketReturn {
  socket: Socket | null;
  connected: boolean;
  error: string | null;
}

const useWebSocket = (url: string): UseWebSocketReturn => {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Create socket connection
    const socket = io(url, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000,
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('WebSocket connected');
      setConnected(true);
      setError(null);
    });

    socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setConnected(false);
      if (reason === 'io server disconnect') {
        // Server disconnected the socket, manual reconnection needed
        socket.connect();
      }
    });

    socket.on('connect_error', (err) => {
      console.error('WebSocket connection error:', err);
      setError(`Connection failed: ${err.message}`);
      setConnected(false);
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts');
      setConnected(true);
      setError(null);
    });

    socket.on('reconnect_error', (err) => {
      console.error('WebSocket reconnection error:', err);
      setError(`Reconnection failed: ${err.message}`);
    });

    socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed');
      setError('Unable to reconnect to server');
      setConnected(false);
    });

    // Join dashboard room for targeted updates
    socket.emit('join-room', 'dashboard');

    // Cleanup on unmount
    return () => {
      socket.emit('leave-room', 'dashboard');
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [url]);

  return {
    socket: socketRef.current,
    connected,
    error
  };
};

export default useWebSocket;